export interface OpportunitiesProps {
  setOpportunityDateRange?:
    | React.Dispatch<
        React.SetStateAction<{
          preset: string;
          startDate: string;
          endDate: string;
        }>
      >
    | ((range: {
        preset: string;
        startDate: string;
        endDate: string;
      }) => void);
  subOrgId?: string;
  dateRange?: {
    startDate: string;
    endDate: string;
  };
  employeePorId?: string;
  isAccountSlideOver?: boolean;
  accountViewState?: any;
  onHeaderActionsChange?: (actions: React.ReactNode) => void;
  sf_account_id?: string;
  startTime?: string;
  endTime?: string;
};

export type OpportunityColumnVisibility = {
  opportunityName: boolean;
  account: boolean;
  arrAmount: boolean;
  opportunityOwner: boolean;
  dealStage: boolean;
  closeDate: boolean;
  nextMeeting: boolean;
  managerForecast: boolean;
}

export interface OpportunitiesParams {
  offset: number;
  limit: number;
  closeDateStart?: string;
  closeDateEnd?: string;
  employeePorId?: string | undefined;
  sortBy: string;
  sortOrder: "asc" | "desc";
  impersonatingPorId?: string | undefined;
  accessLevel?: string | undefined;
  opportunityIds?: string[];
  dealStages?: string[];
  managerForecasts?: string[];
  subOrgIds?: string[];
  opportunityOwners?: string[];
  arrAmountMin?: number;
  arrAmountMax?: number;
  nextMeetingDateRange?: {
    startTime: string;
    endTime: string;
  };
};

export interface RefreshSalesforceOpportunityMappingParams {
  sf_account_id?: string;
  impersonatingPorId?: string;
  accessLevel?: string;
};

export interface StakeholderPerson {
  fullName: string;
  profilePictureUrl?: string | null;
  email?: string;
  designation?: string | null;
}

export interface OpportunityStakeholdersMap {
  oppOwner?: StakeholderPerson | null;
  marketPodLead?: StakeholderPerson | null;
  indiaSp?: StakeholderPerson | null;
  spPodLead?: StakeholderPerson | null;
  [key: string]: StakeholderPerson | null | undefined;
}

export interface OpportunitiesResponse {
  accountName?: string | null;
  opportunityName: string;
  salesforceOpportunityUrl: string;
  arrAmount?: string;
  opportunityOwner: string | null;
  stakeholders?: OpportunityStakeholdersMap | null;
  dealStage: string | null;
  closeDate: string;
  managerForecast: string | null;
  nextInteractionDate: string;
  mappingId: string;
};

export interface OpportunityTimelineInteraction {
  interactionId: string;
  title: string;
  startTime: string;
  overallSentiment: string | null;
}

export interface OpportunityInteractionData {
  interactionId: string;
  title: string;
  isExternal: boolean;
  startTime?: string;
  date?: string;
  endTime?: string;
  duration: number; // in minutes
  rating: string | null;
  ratingSource: string | null;
  isPreviousAsset?: boolean;
  isPresentAsset?: boolean;
}

export interface OpportunityDetailsQueryParams {
  limit: number;
  offset: number;
  sortOrder: "asc" | "desc";
  sortBy: string;
  mappingId: string;
  impersonatingPorId?: string;
  accessLevel?: string;
}

export interface OpportunityDetailsBody {
  startTime: string;
  endTime: string;
}

export interface OpportunityDetailsResponse {
  totalRecords: number;
  interactions: OpportunityTimelineInteraction[];
  interactionData: OpportunityInteractionData[];
  limit: number;
  offset: number;
  [key: string]: any;
}

export interface OpportunityRow {
  id: string;
  opportunityName: string;
  account: string;
  arrAmount: string | number | null;
  opportunityOwner: string;
  stakeholders?: OpportunityStakeholdersMap | null;
  dealStage: string;
  closeDate: string;
  nextMeeting: string;
  managerForecast: string;
  salesforceOpportunityUrl?: string;
}

export interface OpportunityStakeholder {
  id: string;
  name: string;
  role?: string;
  avatarUrl?: string;
  email?: string;
}

export interface OpportunityNavigationState {
  parent_page_identifier?: string;
  ParentPageIdentifier?: string;
  isAccountSlideOver?: boolean;
  activeView?: string;
  accountViewState?: any;

  dateRange?: {
    preset: string;
    startDate: string;
    endDate: string;
  };
  selectedOpportunityIds?: string[];
  selectedAccountIds?: string[];
  selectedOpportunityOwnerIds?: string[];
  selectedDealStages?: string[];
  selectedManagerForecasts?: string[];
  selectedArrAmount?: any;
  nextMeetingRange?: any;
  closeDateRange?: any;
  sort?: { sort_field: string; sort_order?: "asc" | "desc" };
  pageNumber?: number;
  pageSize?: number;
  columnVisibility?: any;
  isSlideOverOpen?: boolean;
  selectedOpportunity?: OpportunityRow | null;
  detailsDateRange?: {
    startDate: string;
    endDate: string;
  };
  detailsPageNumber?: number;
  detailsPageSize?: number;
  detailsSort?: { sort_field: string; sort_order?: "asc" | "desc" };
  salesforceAccountId?: string;
  salesforceAccountUrl?: string;
}

export interface OpportunityDetailsProps {
  opportunity: OpportunityRow | null;
  stakeholders?: OpportunityStakeholder[];
  parentDateRange?: {
    startDate: string;
    endDate: string;
    preset?: string;
  };
  navigationState?: OpportunityNavigationState;
}

export type OpportunitySlideOverProps = OpportunityDetailsProps & {
  isOpen: boolean;
  onClose: () => void;
};

export const STAKEHOLDER_ROLE_LABELS: Record<string, string> = {
  oppOwner: "Opportunity Owner",
  marketPodLead: "Market POD Lead",
  indiaSp: "India SP",
  spPodLead: "SP POD Lead",
};

export const CLOSED_DEAL_STAGES = new Set([
  "9 - closed won",
  "10 - closed lost",
]);
